/**
 * 七层绘制器（spec §7.1）。
 *
 * 从下到上：
 *  1) 底图（选中编辑时半透明）
 *  2) 红色刀线 outline
 *  3) 各组件本体（按 components[] 顺序，分 edit / draw 模式）
 *  4) 冲突红实线矩形（conflictIds 中且非当前选中项）
 *  5) 选中虚线框（无冲突蓝、有冲突红虚线；选中项冲突不在第 4 层画实线）
 *  6) 旋转 icon（仅 selectedId 为 baseHole 时由对应 type 绘制）
 *  7) 放大镜 scaleCanvas（独立 canvas）
 *
 * types 不得跨层绘制；本文件只把每层职责委派给 types 或自身实现。
 */

import {
  DEFAULT_CONFIG,
  DRAW_TYPE,
  modeEdit,
  modeDraw,
  lineColor
} from './constants.js';
import { getComponentType } from './typeRegistry.js';
import { drawSmoothOutlinePath } from '../outlineRender/outlineSmoothRenderer.js';
import { buildDrawableOutlineSegments } from '../outlineRender/outlineRegionSegments.js';

/**
 * 绘制选中/冲突外框。baseHole 为 oriented（随 rotationRad）；其它 type 为轴对齐 aabb。
 * @param {CanvasRenderingContext2D} ctx
 * @param {{kind?:string,x?:number,y?:number,w?:number,h?:number,cx?:number,cy?:number,rotationRad?:number}} rect
 * @param {string} strokeStyle
 * @param {number} lineWidth
 * @param {Array<number>} lineDash
 * @returns {void}
 */
function strokeSelectionRect(ctx, rect, strokeStyle, lineWidth, lineDash) {
  if (!rect) return;
  ctx.save();
  ctx.setLineDash(lineDash || []);
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = Math.max(lineWidth, 1);
  if (rect.kind === 'oriented') {
    ctx.translate(rect.cx, rect.cy);
    ctx.rotate(rect.rotationRad);
    ctx.beginPath();
    ctx.rect(-rect.w / 2, -rect.h / 2, rect.w, rect.h);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * 按高度百分比计算底座孔圆角半径。
 * @param {number} width - 底座孔宽度 px
 * @param {number} height - 底座孔高度 px
 * @returns {number}
 */
function getBaseHoleCornerRadius(width, height) {
  const percent = DEFAULT_CONFIG.baseHoleCornerRadiusPercent;
  const value = typeof percent === 'number' && isFinite(percent) ? percent : 0;
  const raw = height * (Math.max(0, value) / 100);
  return Math.max(0, Math.min(raw, width / 2, height / 2));
}

/**
 * 构建圆角矩形路径；圆角为 0 时退化为普通矩形。
 * @param {CanvasRenderingContext2D} ctx - canvas 上下文
 * @param {number} x - 左上角 x
 * @param {number} y - 左上角 y
 * @param {number} w - 宽度
 * @param {number} h - 高度
 * @param {number} radius - 圆角半径
 * @returns {void}
 */
function buildRoundRectPath(ctx, x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius || 0, w / 2, h / 2));
  ctx.beginPath();
  // 无圆角时保持普通矩形路径，避免改变 0 圆角表现。
  if (r <= 0) {
    ctx.rect(x, y, w, h);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * 创建 renderer。
 * @param {Object} sharedState
 * @returns {{ render: Function }}
 */
export function createOutlineRenderer(sharedState) {
  let baseImageCache = null;

  /**
   * 主入口：一次完整重绘。
   * @param {Object} args
   * @param {Array<Object>} args.components
   * @param {string|null} args.selectedId
   * @param {string[]} args.conflictIds
   * @param {boolean} args.enableMagnifierGlobal
   * @param {Array<Object>} [args.holeRegions] - 孔位区域，刀线绘制时据此拆开开放片段
   * @returns {Promise<void>}
   */
  async function render({
    components,
    selectedId,
    conflictIds,
    enableMagnifierGlobal,
    holeRegions
  }) {
    if (sharedState.destroyed) return;
    const ctx = sharedState.ctx;
    ctx.clearRect(
      -sharedState.canvasRoundWhiteSpace,
      -sharedState.canvasRoundWhiteSpace,
      sharedState.canvasWidth,
      sharedState.canvasHeight
    );

    // 1) 底图
    await drawBaseImage(selectedId);

    // 1.5) draw 模式下，穿孔/底座孔抠除底图（destination-out 打孔）
    if (sharedState.mode === modeDraw) {
      punchImageHoles(components);
    }

    // 2) 刀线（draw 模式下跳过孔位区域内的点）
    drawOutline(holeRegions);

    // 3) 各组件本体
    for (const inst of components) {
      const strategy = getComponentType(inst.type);
      if (!strategy) continue;
      if (sharedState.mode === modeEdit) {
        if (typeof strategy.drawEdit === 'function') {
          strategy.drawEdit(inst, ctx, sharedState);
        }
      } else if (sharedState.mode === modeDraw) {
        if (typeof strategy.drawPreview === 'function') {
          strategy.drawPreview(inst, ctx, sharedState);
        }
      }
    }

    // 4) 冲突红实线矩形（未选中的冲突组件；选中项由第 5 层红虚线表示）
    if (sharedState.mode === modeEdit) {
      const conflictSet = new Set(conflictIds || []);
      for (const inst of components) {
        if (!conflictSet.has(inst.id)) continue;
        if (selectedId && inst.id === selectedId) continue;
        drawConflictBox(inst);
      }
    }

    // 5) 选中虚线框
    if (sharedState.mode === modeEdit && selectedId) {
      const selected = components.find((c) => c.id === selectedId);
      if (selected) {
        const isConflict =
          conflictIds && conflictIds.indexOf(selectedId) !== -1;
        drawSelectionBox(selected, isConflict);
      }
    }

    // 6) 旋转 icon（仅 baseHole）
    if (sharedState.mode === modeEdit && selectedId) {
      const selected = components.find((c) => c.id === selectedId);
      if (selected && selected.type === DRAW_TYPE.BASE_HOLE) {
        const strategy = getComponentType(selected.type);
        if (strategy && typeof strategy.drawActionIcon === 'function') {
          strategy.drawActionIcon(selected, ctx, sharedState);
        }
      }
    }

    // 7) 放大镜
    renderMagnifierIfNeeded({
      components,
      selectedId,
      enableMagnifierGlobal
    });

    drawDebugCoordinateSystem();
  }

  /**
   * 绘制底图，selectedId 存在时叠 0.5 透明（spec §4.3）。
   * @param {string|null} selectedId
   * @returns {Promise<void>}
   */
  function drawBaseImage(selectedId) {
    const ctx = sharedState.ctx;
    const drawIt = () => {
      const needAlpha = sharedState.mode === modeEdit && !!selectedId;
      if (needAlpha) {
        ctx.save();
        ctx.globalAlpha = DEFAULT_CONFIG.imageEditOpacity;
      }
      ctx.drawImage(
        baseImageCache,
        0,
        0,
        sharedState.imageInfo.width,
        sharedState.imageInfo.height,
        sharedState.imageLeft,
        sharedState.imageTop,
        sharedState.drawImageWidth,
        sharedState.drawImageHeight
      );
      if (needAlpha) {
        ctx.restore();
      }
    };

    if (baseImageCache) {
      drawIt();
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        baseImageCache = img;
        drawIt();
        resolve();
      };
      img.onerror = function () {
        console.error(
          '[serviceComponentHole] 底图加载失败:',
          sharedState.imageSrc
        );
        resolve();
      };
      img.src = sharedState.imageSrc;
    });
  }

  /**
   * draw 模式下对穿孔/底座孔执行 destination-out 抠除底图。
   * 穿孔：填充圆；底座孔：填充旋转矩形。
   * @param {Array<Object>} components
   * @returns {void}
   */
  function punchImageHoles(components) {
    var ctx = sharedState.ctx;
    for (var i = 0; i < components.length; i++) {
      var inst = components[i];
      if (inst.type === DRAW_TYPE.PERFORATION) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(
          inst.centerPoint.x,
          inst.centerPoint.y,
          inst.radius,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.restore();
      } else if (inst.type === DRAW_TYPE.BASE_HOLE) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.translate(inst.centerPoint.x, inst.centerPoint.y);
        ctx.rotate(inst.rotationRad);
        buildRoundRectPath(
          ctx,
          -inst.width / 2,
          -inst.height / 2,
          inst.width,
          inst.height,
          getBaseHoleCornerRadius(inst.width, inst.height)
        );
        ctx.fill();
        ctx.restore();
      } else if (inst.type === DRAW_TYPE.KEY_HOLE) {
        // 钥匙扣内圆镂空（环形金属扣，内圆为贯穿孔）
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(
          inst.centerPoint.x,
          inst.centerPoint.y,
          inst.inRadius,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.restore();
      }
    }
  }

  /**
   * 红色刀线。使用 smoothRdp 路径描边减少像素台阶；draw 模式按孔位区域拆片段。
   * @param {Array<Object>} [holeRegions] - 预览模式下用于拆开刀线的孔位区域
   * @returns {void}
   */
  function drawOutline(holeRegions) {
    const ctx = sharedState.ctx;
    const points = sharedState.outlinePoints;
    if (!points || !points.length) return;
    ctx.save();
    const currentLineColor = sharedState.lineColor || lineColor;
    ctx.strokeStyle = currentLineColor;
    ctx.lineWidth = Math.max(sharedState.lineWidth, 1);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.setLineDash([]);

    const segments = buildDrawableOutlineSegments(points, holeRegions);
    for (const segment of segments) {
      drawSmoothOutlinePath(ctx, segment.points, segment.closed, ctx.lineWidth);
    }

    ctx.restore();
  }

  /**
   * 红实线冲突矩形（仅非选中冲突组件；外接矩形由 type.getSelectionRect 提供）。
   * @param {Object} instance
   * @returns {void}
   */
  function drawConflictBox(instance) {
    const strategy = getComponentType(instance.type);
    if (!strategy || typeof strategy.getSelectionRect !== 'function') return;
    const rect = strategy.getSelectionRect(instance, sharedState);
    if (!rect) return;
    strokeSelectionRect(
      sharedState.ctx,
      rect,
      DEFAULT_CONFIG.conflictColor,
      sharedState.lineWidth,
      []
    );
  }

  /**
   * 选中虚线框：无冲突为蓝虚线；有冲突为红虚线（不画红实线，与第 4 层区分）。
   * @param {Object} instance
   * @param {boolean} isConflict
   * @returns {void}
   */
  function drawSelectionBox(instance, isConflict) {
    const strategy = getComponentType(instance.type);
    if (!strategy || typeof strategy.getSelectionRect !== 'function') return;
    const rect = strategy.getSelectionRect(instance, sharedState);
    if (!rect) return;
    strokeSelectionRect(
      sharedState.ctx,
      rect,
      isConflict
        ? DEFAULT_CONFIG.conflictColor
        : DEFAULT_CONFIG.selectionBoxColor,
      sharedState.lineWidth,
      DEFAULT_CONFIG.selectionLineDash
    );
  }

  /**
   * 将后端坐标系点位反算到当前 canvas 坐标系。
   * @param {{x:number,y:number}} point - 后端坐标点
   * @param {{offsetX:number,offsetY:number}} offset - SKU 适配补边
   * @returns {{x:number,y:number}} canvas 坐标点
   */
  function convertBackendToCanvas(point, offset) {
    return {
      x: (point.x - offset.offsetX) * sharedState.canvasScale,
      y: (point.y - offset.offsetY) * sharedState.canvasScale
    };
  }

  /**
   * 绘制 slotRect 百分比计算使用的后端归一化坐标系。
   * @returns {void}
   */
  function drawDebugCoordinateSystem() {
    // 仅 debug 模式绘制坐标系辅助线，避免影响常规编辑体验。
    if (!sharedState.debug) return;
    const ctx = sharedState.ctx;
    const imageRatio =
      sharedState.imageInfo.width / sharedState.imageInfo.height;
    const skuRatio =
      sharedState.template.paperWidth / sharedState.template.paperHeight;
    let offsetX = 0;
    let offsetY = 0;
    // 图片比 SKU 更宽时，后端坐标系在高度方向补边；否则在宽度方向补边。
    if (imageRatio > skuRatio) {
      offsetY =
        (sharedState.imageInfo.width / skuRatio -
          sharedState.imageInfo.height) /
        2;
    } else {
      offsetX =
        (sharedState.imageInfo.height * skuRatio -
          sharedState.imageInfo.width) /
        2;
    }
    const baseWidth = sharedState.imageRatioOver
      ? sharedState.imageInfo.width
      : sharedState.imageInfo.height * skuRatio;
    const baseHeight = sharedState.imageRatioOver
      ? sharedState.imageInfo.width / skuRatio
      : sharedState.imageInfo.height;
    const offset = { offsetX, offsetY };
    const topLeft = convertBackendToCanvas({ x: 0, y: 0 }, offset);
    const topRight = convertBackendToCanvas({ x: baseWidth, y: 0 }, offset);
    const bottomRight = convertBackendToCanvas(
      { x: baseWidth, y: baseHeight },
      offset
    );
    const bottomLeft = convertBackendToCanvas({ x: 0, y: baseHeight }, offset);
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.rect(
      topLeft.x,
      topLeft.y,
      topRight.x - topLeft.x,
      bottomLeft.y - topLeft.y
    );
    ctx.stroke();
    drawDebugExtendedLine(topLeft, topRight);
    drawDebugExtendedLine(topRight, bottomRight);
    drawDebugExtendedLine(bottomRight, bottomLeft);
    drawDebugExtendedLine(bottomLeft, topLeft);
    ctx.restore();
  }

  /**
   * 沿两点所在直线向外延伸到 canvas 边界并绘制。
   * @param {{x:number,y:number}} p1 - 线段起点
   * @param {{x:number,y:number}} p2 - 线段终点
   * @returns {void}
   */
  function drawDebugExtendedLine(p1, p2) {
    const ctx = sharedState.ctx;
    const minX = -sharedState.canvasRoundWhiteSpace;
    const minY = -sharedState.canvasRoundWhiteSpace;
    const maxX = sharedState.canvasWidth - sharedState.canvasRoundWhiteSpace;
    const maxY = sharedState.canvasHeight - sharedState.canvasRoundWhiteSpace;
    // 水平线按左右边界延伸，便于观察基准高度所在位置。
    if (Math.abs(p1.y - p2.y) < 0.001) {
      ctx.beginPath();
      ctx.moveTo(minX, p1.y);
      ctx.lineTo(maxX, p1.y);
      ctx.stroke();
      return;
    }
    // 垂直线按上下边界延伸，便于观察基准宽度所在位置。
    if (Math.abs(p1.x - p2.x) < 0.001) {
      ctx.beginPath();
      ctx.moveTo(p1.x, minY);
      ctx.lineTo(p1.x, maxY);
      ctx.stroke();
    }
  }

  /**
   * 放大镜：仅 edit + 选中 + 当前 instance.isDragging + enableMagnifier 都满足时显示。
   * @param {Object} args
   * @returns {void}
   */
  function renderMagnifierIfNeeded({
    components,
    selectedId,
    enableMagnifierGlobal
  }) {
    const scaleCanvas = sharedState.scaleCanvas;
    if (!scaleCanvas) return;

    let visible = false;
    let anchor = null;
    let strategy = null;
    let instance = null;

    if (enableMagnifierGlobal && sharedState.mode === modeEdit && selectedId) {
      instance = components.find((c) => c.id === selectedId);
      if (
        instance &&
        instance.isDragging &&
        instance.enableMagnifier !== false
      ) {
        strategy = getComponentType(instance.type);
        if (strategy && typeof strategy.getMagnifierAnchor === 'function') {
          anchor = strategy.getMagnifierAnchor(instance, sharedState);
          if (anchor) visible = true;
        }
      }
    }

    if (!visible) {
      scaleCanvas.style.opacity = 0;
      return;
    }

    const scaleCtx = scaleCanvas.getContext('2d');
    scaleCtx.clearRect(0, 0, scaleCanvas.width, scaleCanvas.height);
    const scaleFactor = 1.2;
    const radius = scaleCanvas.clientWidth / 2;
    const sourceX = anchor.x - radius / scaleFactor;
    const sourceY = anchor.y - radius / scaleFactor;
    const sourceW = (radius * 2) / scaleFactor;
    const sourceH = (radius * 2) / scaleFactor;
    // 主 canvas 缓冲区为物理像素，source 坐标需从 CSS 像素转换为物理像素
    const dpr = sharedState.dpr;
    scaleCtx.drawImage(
      sharedState.canvas,
      (sourceX + sharedState.canvasRoundWhiteSpace) * dpr,
      (sourceY + sharedState.canvasRoundWhiteSpace) * dpr,
      sourceW * dpr,
      sourceH * dpr,
      0,
      0,
      scaleCanvas.width,
      scaleCanvas.height
    );
    scaleCanvas.style.opacity = 1;
  }

  return { render };
}
